<!-- frontend/src/App.vue -->

<script setup lang="ts">
import { RouterLink, RouterView } from 'vue-router'
import { useGameStore } from '@/stores/game'
import { useRoomStore } from '@/stores/room'
import { useAuthStore } from '@/stores/auth' // AuthStore追加
import { useI18n } from 'vue-i18n'
import { watchEffect } from 'vue'

const gameStore = useGameStore()
const roomStore = useRoomStore()
const authStore = useAuthStore() // AuthStore追加
const { locale, t } = useI18n()

// 言語切り替え時にタイトルとメタ説明文を更新
watchEffect(() => {
  document.title = t('meta.title');
  const descriptionMeta = document.querySelector('meta[name="description"]');
  if (descriptionMeta) {
    descriptionMeta.setAttribute('content', t('meta.description'));
  }
});

const changeLanguage = (event: Event) => {
  const target = event.target as HTMLSelectElement;
  locale.value = target.value;
};

// 開発用: 正常切断
const testNormalDisconnect = () => {
  console.log('[DEV] 正常切断をテスト中...')
  gameStore.disconnectWebSocket()
  roomStore.disconnectRoomWebSocket()
  alert('正常切断しました')
}

// 開発用: 異常切断(ネットワークオフラインをシミュレート)
const testAbnormalDisconnect = () => {
  console.log('[DEV] 異常切断をテスト中...')

  // gameStoreのWebSocketを強制的にクローズ
  // gameStoreではwebsocketがrefなので、内部のstateから直接アクセス
  const gameWs = gameStore.websocket
  if (gameWs && gameWs.readyState === WebSocket.OPEN) {
    // 再接続を防ぐため、oncloseハンドラを無効化
    gameWs.onclose = null
    gameWs.onerror = null
    // 異常切断をシミュレート(code 4000はカスタムコード)
    // 1006は予約済みコードなので使えない。3000-4999の範囲を使用
    gameWs.close(4000, 'Abnormal disconnect test')
    console.log('[DEV] gameStore WebSocket強制クローズ完了')
  } else {
    console.log('[DEV] gameStore WebSocketは接続されていません')
  }

  // roomStoreのWebSocketを強制的にクローズ
  const roomWs = roomStore.websocket
  if (roomWs && roomWs.readyState === WebSocket.OPEN) {
    // 再接続を防ぐため、oncloseハンドラを無効化
    roomWs.onclose = null
    roomWs.onerror = null
    // 異常切断をシミュレート(code 4000はカスタムコード)
    roomWs.close(4000, 'Abnormal disconnect test')
    console.log('[DEV] roomStore WebSocket強制クローズ完了')
  } else {
    console.log('[DEV] roomStore WebSocketは接続されていません')
  }

  alert('異常切断しました(WebSocketを強制クローズ)')
}

// 開発モード判定
const isDev = import.meta.env.DEV
const isEmbed = import.meta.env.VITE_IS_EMBED === 'true' // 文字列として読み込まれるため比較が必要
import AdBanner from './components/AdBanner.vue'
</script>

<template>
  <div v-if="authStore.loading && !authStore.isAuthenticated" class="loading-overlay">
    <div class="loading-spinner">Loading...</div>
  </div>
  <div v-else>
    <header v-if="!isEmbed">
      <!-- 固定バナー広告 -->
      <AdBanner />
    </header>


    <!-- メインコンテンツラッパー: 言語切り替えスイッチをオーバーレイするための基準 -->
    <div class="view-wrapper">
      <!-- メインコンテンツ上部: 言語切り替えスイッチ -->
      <!-- オーバーレイ表示のため、RouterViewと同じコンテナ内に配置し、絶対配置を行う -->
      <div class="main-content-controls">
        <div class="language-selector">
          <select v-model="locale" @change="changeLanguage">
            <option value="ja">日本語</option>
            <option value="en">English</option>
          </select>
        </div>
      </div>

      <RouterView />
    </div>

    <footer v-if="!isEmbed">
      <div class="wrapper">
        <nav>
          <RouterLink to="/">{{ $t('footer.home') }}</RouterLink>
          <RouterLink to="/privacy">{{ $t('footer.privacy') }}</RouterLink>
          <RouterLink to="/about">{{ $t('footer.about') }}</RouterLink>
          <RouterLink to="/contact">{{ $t('footer.contact') }}</RouterLink>
        </nav>



        <!-- 開発用テストボタン -->
        <div v-if="false" class="dev-buttons">
          <button @click="testNormalDisconnect" class="dev-btn normal">正常切断</button>
          <button @click="testAbnormalDisconnect" class="dev-btn abnormal">異常切断</button>
        </div>
      </div>
    </footer>

    <!-- 開発モードバッジ -->
    <div v-if="isDev" class="dev-mode-badge" title="Development Mode (Hot Reload)">
      DEV
    </div>
  </div>
</template>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  font-size: 1.5rem;
  color: #333;
}

header {
  width: 100%;
  margin-top: 3px;
  /* Added 3px top margin */
  margin-bottom: 5px;
  /* 3px -> 5px */
}

/* メインコンテンツラッパー */
.view-wrapper {
  position: relative;
  /* 子要素の絶対配置の基準にする */
  width: 100%;
  /* コンテンツ幅を確保 */
}

footer {
  margin-top: 1rem;
  border-top: 1px solid #e0e0e0;
  background-color: #fff;
  padding: 0.5rem 0;
}

.wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  max-width: 1200px;
  margin: 0 auto;
  flex-wrap: wrap;
  gap: 0.5rem;
}

nav {
  display: flex;
  gap: 1rem;
}

nav a {
  text-decoration: none;
  color: #2c3e50;
  font-size: 0.95rem;
  font-weight: 500;
  transition: color 0.3s ease;
  position: relative;
  padding: 0.2rem 0;
}

nav a::after {
  content: '';
  position: absolute;
  width: 100%;
  transform: scaleX(0);
  height: 2px;
  bottom: 0;
  left: 0;
  background-color: #17a2b8;
  transform-origin: bottom right;
  transition: transform 0.25s ease-out;
}

nav a:hover {
  color: #17a2b8;
}

nav a:hover::after {
  transform: scaleX(1);
  transform-origin: bottom left;
}

/* 言語切り替えセレクタ */
.language-selector select {
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  border: 1px solid #ccc;
  background-color: white;
  font-size: 0.8rem;
  cursor: pointer;
}

/* 開発用ボタン */
.dev-buttons {
  display: flex;
  gap: 0.5rem;
}

.dev-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.dev-btn.normal {
  background-color: #4CAF50;
  color: white;
}

.dev-btn.normal:hover {
  background-color: #45a049;
}

.dev-btn.abnormal {
  background-color: #f44336;
  color: white;
}

.dev-btn.abnormal:hover {
  background-color: #da190b;
}

/* 開発モードバッジ */
.dev-mode-badge {
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: rgba(255, 140, 0, 0.8);
  /* オレンジ色 */
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-weight: bold;
  font-size: 0.7rem;
  z-index: 10000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid white;
  pointer-events: none;
  /* クリックを邪魔しない */
}

/* メインコンテンツ上部のコントロールエリア（絶対配置でオーバーレイ） */
.main-content-controls {
  position: absolute;
  top: 10px;
  right: 10px;
  max-width: 1200px;
  /* margin: 0 auto; 不要になるが、親の幅次第 */
  width: auto;
  padding: 0;
  display: flex;
  justify-content: flex-end;
  z-index: 10;
  /* 他の要素より上に */
}
</style>
<style>
/* Global Reset */
body {
  margin: 0;
  padding: 0;
  font-family: sans-serif;
}

#app {
  max-width: 100vw;
  margin: 0 auto;
  padding: 0;
  font-weight: normal;
}
</style>
